import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { supabase, saveChatHistory, getChatHistory } from '../supabaseClient';
import VoiceCommand from './VoiceCommand';
import AIChallenges from './AIChallenges';
import Matchmaking from './Matchmaking';
import SpeedrunTips from './SpeedrunTips';
import LiveUpdates from './LiveUpdates';
import MemeGenerator from './MemeGenerator';

const GamingChatbot = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState('chat'); // chat, voice, challenges, matchmaking, speedrun, updates, memes
  const chatContainerRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [signupCooldown, setSignupCooldown] = useState(0);
  const [isSigningUp, setIsSigningUp] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (session) loadChatHistory(session.user.email);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) loadChatHistory(session.user.email);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    let cooldownTimer;
    if (signupCooldown > 0) {
      cooldownTimer = setInterval(() => {
        setSignupCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(cooldownTimer);
  }, [signupCooldown]);

  const loadChatHistory = async (email) => {
    const history = await getChatHistory(email);
    if (history && history.length > 0) {
      setMessages(history);
    }
  };

  const handleSignUp = async (email, password) => {
    if (signupCooldown > 0) {
      setErrorMessage(`Please wait ${signupCooldown} seconds before trying again.`);
      return;
    }

    try {
      setIsSigningUp(true);
      setErrorMessage('');
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin
        }
      });
      
      if (error) {
        if (error.message.includes('security purposes')) {
          const waitTime = parseInt(error.message.match(/\d+/)[0]) || 60;
          setSignupCooldown(waitTime);
          setErrorMessage(`Please wait ${waitTime} seconds before trying to sign up again.`);
        } else {
          setErrorMessage(error.message);
        }
        throw error;
      }
      
      setErrorMessage('Check your email for the confirmation link!');
    } catch (error) {
      console.error('Error signing up:', error.message);
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleSignIn = async (email, password) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error signing in:', error.message);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setMessages([]);
    } catch (error) {
      console.error('Error signing out:', error.message);
    }
  };

  const fetchAIResponse = async (query) => {
    try {
      const response = await axios.post(
        'https://api-inference.huggingface.co/models/mistralai/Mistral-7B-Instruct-v0.2',
        {
          inputs: `[INST]You are XKID GAMING AI, a gaming expert who provides detailed game guides, meta strategies, and gaming advice. Your responses should be engaging and reflect gaming culture.

User query: ${query}[/INST]`
        },
        {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_HUGGINGFACE_API_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Extract the response text from the model output
      const aiResponse = response.data[0]?.generated_text || 'Sorry, I could not generate a response.';
      
      // Clean up the response by removing the instruction and only keeping the actual response
      const cleanResponse = aiResponse.split('[/INST]').pop().trim();
      
      return cleanResponse;
    } catch (error) {
      console.error('Error fetching AI response:', error);
      return 'Sorry, I encountered an error. Please try again.';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = { message: inputMessage, is_ai: false, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    await saveChatHistory(session.user.email, inputMessage, false);
    setInputMessage('');
    setIsLoading(true);

    const aiResponse = await fetchAIResponse(inputMessage);
    const aiMessage = { message: aiResponse, is_ai: true, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, aiMessage]);
    await saveChatHistory(session.user.email, aiResponse, true);
    setIsLoading(false);
  };

  const handleVoiceCommand = async (transcript) => {
    // Switch to chat view first
    setActiveFeature('chat');
    
    // Set the transcript as input message
    setInputMessage(transcript);
    
    // Small delay to ensure state is updated
    setTimeout(() => {
      // Submit the message
      handleSendMessage({ preventDefault: () => {} });
    }, 100);
  };

  const renderActiveFeature = () => {
    if (!session) return null;

    switch (activeFeature) {
      case 'voice':
        return <VoiceCommand onCommand={handleVoiceCommand} />;
      case 'challenges':
        return <AIChallenges userId={session.user.id} />;
      case 'matchmaking':
        return <Matchmaking userId={session.user.id} />;
      case 'speedrun':
        return <SpeedrunTips userId={session.user.id} />;
      case 'updates':
        return <LiveUpdates />;
      case 'memes':
        return <MemeGenerator userId={session.user.id} />;
      default:
        return (
          <>
            <div 
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 bg-grid-pattern bg-grid"
            >
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.is_ai ? 'justify-start' : 'justify-end'} mb-4`}
                >
                  <div
                    className={`max-w-[80%] p-4 rounded-lg shadow-lg ${
                      msg.is_ai
                        ? 'bg-xkid-gray-light text-white border-l-4 border-xkid-green'
                        : 'bg-xkid-green bg-opacity-90 text-black font-medium'
                    }`}
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {msg.is_ai ? (
                      <div className="space-y-2">
                        <div className="text-xs text-xkid-green mb-1">XKID AI</div>
                        <div className="leading-relaxed">{msg.message}</div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-xs text-black text-opacity-70 mb-1">You</div>
                        <div>{msg.message}</div>
                      </div>
                    )}
                    <div className="text-xs mt-2 opacity-50">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start mb-4">
                  <div className="max-w-[80%] p-4 rounded-lg shadow-lg bg-xkid-gray-light border-l-4 border-xkid-green">
                    <div className="text-xs text-xkid-green mb-1">XKID AI</div>
                    <div className="flex items-center space-x-2">
                      <div className="text-white">Thinking</div>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-xkid-green rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-xkid-green rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-xkid-green rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleSendMessage} className="p-4 border-t border-xkid-green bg-xkid-gray">
              <div className="flex space-x-4">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Ask me anything about gaming..."
                  className="flex-1 p-4 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
                />
                <button
                  type="submit"
                  className="px-6 py-4 bg-xkid-green text-black rounded-lg font-bold hover:bg-xkid-green-dark transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  Send
                </button>
              </div>
            </form>
          </>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-xkid-black">
        <div className="animate-pulse text-xkid-green text-2xl">Loading XKID GAMING...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-xkid-black bg-grid-pattern bg-grid p-4">
        <div className="animate-float mb-8">
          <h1 className="text-5xl font-bold text-xkid-green animate-glow mb-2">XKID GAMING</h1>
          <p className="text-xkid-green-dark text-xl text-center">Your Ultimate Gaming AI Companion</p>
        </div>
        
        <div className="w-full max-w-md space-y-8 bg-xkid-gray p-8 rounded-lg border border-xkid-green shadow-lg">
          <form onSubmit={(e) => {
            e.preventDefault();
            const email = e.target.email.value;
            const password = e.target.password.value;
            if (e.nativeEvent.submitter.name === 'signup') {
              handleSignUp(email, password);
            } else {
              handleSignIn(email, password);
            }
          }} className="space-y-4">
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email"
              className="w-full p-4 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
            />
            <input
              type="password"
              id="password"
              name="password"
              placeholder="Password"
              className="w-full p-4 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
            />
            <button
              type="submit"
              name="signin"
              className="w-full p-4 rounded-lg bg-xkid-green text-black font-bold hover:bg-xkid-green-dark transition-colors"
            >
              Sign In
            </button>
            <button
              type="submit"
              name="signup"
              disabled={signupCooldown > 0 || isSigningUp}
              className={`w-full p-4 rounded-lg bg-xkid-red text-white font-bold transition-colors ${
                signupCooldown > 0 || isSigningUp
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-xkid-red-dark'
              }`}
            >
              {signupCooldown > 0
                ? `Sign Up (${signupCooldown}s)`
                : isSigningUp
                ? 'Signing Up...'
                : 'Sign Up'}
            </button>
          </form>
          {errorMessage && (
            <div className={`p-4 rounded-lg ${
              errorMessage.includes('Check your email') 
                ? 'bg-xkid-green bg-opacity-20 text-xkid-green' 
                : 'bg-xkid-red bg-opacity-20 text-xkid-red'
            }`}>
              {errorMessage}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-xkid-black">
      <header className="bg-xkid-gray border-b border-xkid-green p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-xkid-green text-xl md:text-2xl font-bold animate-glow">XKID GAMING</h1>
          <button
            onClick={handleSignOut}
            className="px-3 py-1 md:px-4 md:py-2 bg-xkid-red text-white text-sm md:text-base rounded hover:bg-xkid-red-dark transition-colors"
          >
            Sign Out
          </button>
        </div>
        <div className="relative mt-4">
          <div className="flex space-x-2 md:space-x-4 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
            {['Chat', 'Voice', 'Challenges', 'Matchmaking', 'Speedrun', 'Updates', 'Memes'].map((feature) => (
              <button
                key={feature}
                onClick={() => setActiveFeature(feature.toLowerCase())}
                className={`px-2 py-1 md:px-4 md:py-2 rounded-full transition-colors whitespace-nowrap text-sm md:text-base flex-shrink-0 ${
                  activeFeature === feature.toLowerCase()
                    ? 'bg-xkid-green text-black'
                    : 'bg-xkid-gray-light text-xkid-green hover:bg-xkid-gray'
                }`}
              >
                {feature}
              </button>
            ))}
          </div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-xkid-gray pointer-events-none"></div>
        </div>
      </header>

      {renderActiveFeature()}
    </div>
  );
};

export default GamingChatbot; 